import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { getMonthName } from '../../../shared/utils/dates.util';
import Text from '../../../components/atoms/Text';
import Tag from '../../../components/molecules/Tag';
import CALENDAR from '../../../shared/constants/calendar';
import TagIconCalendar from '../atoms/TagIconCalendar';
import Divider from '../../../components/atoms/Divider';
import Button from '../../../components/atoms/Button';
import { CaretCircleLeft, XCircle } from '@phosphor-icons/react';

import MonthlyDetail from './MonthlyDetail';
import { setSelectedPeriod } from '../../../redux/slices/taxCalendar.slice';
import { TPeriodicity } from '../../../@types/Calendar.type';
import { useMediaQuery } from '@mui/material';
import Container from '../../../components/atoms/Container';
import {
	ANALYTICS_EVENTS,
	EVENT_TYPES,
} from '../../../shared/constants/events';
import { UserRegime } from '../../../@types/TaxProfile';
import { logClevertapEvent } from '../../../shared/analytics/analytics';
import { REGULARIZATION_REGARATION_PRODUCTS_SLUG } from '../../../shared/constants/SlugsProducts';
import { REGIMES_CODE } from '../../../@types/Regime.type';
import useAddToCart from '../../../hooks/useAddToCart';
import { setShowModalCheckout } from '../../../redux/slices/plans.slice';
import { RegimeFFT } from '../../../@types/Products.type';

interface Props {
	periodsAdquired: any[];
}

const PeriodDetail: React.FC<Props> = ({ periodsAdquired }) => {
	const dispatch = useDispatch();
	const isMobile = useMediaQuery('(max-width: 1024px)');

	const {
		month,
		year,
		period,
		annualYearSelected,
		selectedPeriod,
		statusByPriotityMonth,
	} = useSelector((state: RootState) => state.taxCalendar);
	const userRegimes = useSelector(
		(state: RootState) => state.taxProfile.userRegimes,
	);

	const { handleAddProductBySlug, isLoading: isLoadingAddToCart } =
		useAddToCart();

	const periodTagStatus = () => {
		if (period === TPeriodicity.MONTHLY) {
			const periodAdquired = periodsAdquired.find(
				(period: any) => period.month === month && period.year === year,
			);

			const isPeriodAdquired =
				statusByPriotityMonth === 'PENDIENTE' && periodAdquired;
			return (
				<Tag
					label={
						isPeriodAdquired
							? 'Adquirido'
							: CALENDAR.taxboardDeclarationStatusLabel[statusByPriotityMonth]
					}
					type='filled'
					caseVariant={
						isPeriodAdquired
							? 'loading'
							: (CALENDAR.labelColor[statusByPriotityMonth] ?? 'filter')
					}
				/>
			);
		}

		const annualStatus = selectedPeriod?.year?.selectedYear?.status;
		return (
			<Tag
				label={CALENDAR.taxboardDeclarationStatusLabel[annualStatus]}
				type='filled'
				caseVariant={CALENDAR.labelColor[annualStatus] ?? 'filter'}
				iconRight={<TagIconCalendar status={annualStatus} />}
				quantity={1}
			/>
		);
	};

	const handleCloseDetail = () => {
		dispatch(
			setSelectedPeriod({
				year: {
					...selectedPeriod.year,
					selectedYear: null,
					selectedDeclaration: null,
				},
				month: {
					...selectedPeriod.month,
					detail: null,
					detailByRegime: null,
				},
			}),
		);
	};

	const handleAddToCart = async (regime?: string) => {
		logClevertapEvent(ANALYTICS_EVENTS.TAX_DASHBOARD, {
			event_type: EVENT_TYPES.TAXBOARD_ACQUIRE_PERIOD,
			source: 'declaraciones_mensuales',
			regime:
				userRegimes?.map((regime: UserRegime) => regime?.name).join(' - ') ||
				'[]',
		});

		const slugsPerRegime: Record<string, string> = {
			'Plataformas Tecnológicas':
				REGULARIZATION_REGARATION_PRODUCTS_SLUG.REG_PT_TODAS_ACTIVIDADES,
			business: REGULARIZATION_REGARATION_PRODUCTS_SLUG.REG_ACT_EMP,
			resico: REGULARIZATION_REGARATION_PRODUCTS_SLUG.REG_RESICO_PLUS,
			lease: REGULARIZATION_REGARATION_PRODUCTS_SLUG.REG_ARR_MES,
			rif: REGULARIZATION_REGARATION_PRODUCTS_SLUG.REG_RIF,
		};

		await handleAddProductBySlug(
			slugsPerRegime[regime || 'Plataformas Tecnológicas'] ||
				REGULARIZATION_REGARATION_PRODUCTS_SLUG.REG_PT_TODAS_ACTIVIDADES,
			{
				year: year,
				month: month,
				regimes: [RegimeFFT[REGIMES_CODE.PLATAFORMAS_TECNOLOGICAS]],
			},
			false,
		);

		dispatch(setShowModalCheckout(true));
	};

	return (
		<>
			{isMobile && (
				<button
					onClick={handleCloseDetail}
					className='flex items-center gap-1'
				>
					<CaretCircleLeft
						size={20}
						weight='fill'
						color='var(--h-primary)'
					/>
					<Text
						size='caption'
						color='medium'
						weight='medium'
					>
						Volver al calendario
					</Text>
				</button>
			)}
			<Container sx={`flex-1 !p-0 lg:!p-4`}>
				<section className='bg-h-neutral-95 p-4 rounded-lg shadow-md h-full flex flex-col justify-between'>
					<div>
						<div className='flex flex-row justify-between mb-4 items-center gap-2'>
							<div className='inline-flex gap-2'>
								{period === TPeriodicity.MONTHLY && (
									<Text
										size='body-1'
										color='medium'
										weight='bold'
										sx='capitalize'
									>
										{getMonthName(month)}
									</Text>
								)}
								<Text
									size={period === TPeriodicity.MONTHLY ? 'caption' : 'body-1'}
									color={period === TPeriodicity.MONTHLY ? 'light' : 'medium'}
									weight={period === TPeriodicity.MONTHLY ? 'medium' : 'bold'}
									sx='mr-4'
								>
									{period === TPeriodicity.YEARLY ? annualYearSelected : year}
								</Text>

								{periodTagStatus()}
							</div>
							{!isMobile && (
								<Button
									onClick={handleCloseDetail}
									label=''
									variant='text'
									labelColor='gray'
									labelSize='caption'
									beforeIcon={
										<XCircle
											size={26}
											weight='fill'
											color='var(--h-gray-40)'
										/>
									}
								></Button>
							)}
						</div>
						<Divider />
						{period === TPeriodicity.MONTHLY && (
							<MonthlyDetail
								periodsAdquired={periodsAdquired}
								handleAddToCart={handleAddToCart}
								isLoadingAddToCart={isLoadingAddToCart.products}
							/>
						)}
					</div>
				</section>
			</Container>
		</>
	);
};

export default PeriodDetail;
